import { Component } from '@theme/component';

/**
 * Updates the recently viewed products in localStorage.
 */
export class RecentlyViewed {
  /** @static @constant {string} The key used to store the viewed products in session storage */
  static #STORAGE_KEY = 'viewedProducts';
  /** @static @constant {number} The maximum number of products to store */
  static #MAX_PRODUCTS = 10;

  /**
   * Adds a product to the recently viewed products list.
   * @param {string} productId - The ID of the product to add.
   */
  static addProduct(productId) {
    let viewedProducts = this.getProducts();

    viewedProducts = viewedProducts.filter((/** @type {string} */ id) => id !== productId);
    viewedProducts.unshift(productId);
    viewedProducts = viewedProducts.slice(0, this.#MAX_PRODUCTS);

    localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(viewedProducts));
  }

  static clearProducts() {
    localStorage.removeItem(this.#STORAGE_KEY);
  }

  /**
   * Retrieves the list of recently viewed products from session storage.
   * @returns {string[]} The list of viewed products.
   */
  static getProducts() {
    return JSON.parse(localStorage.getItem(this.#STORAGE_KEY) || '[]');
  }
}

class RecentlyViewedComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#loadProducts();
  }

  async #loadProducts() {
    const { sectionId, maxProducts, excludeProductId } = this.dataset;
    let viewedProducts = RecentlyViewed.getProducts();

    if (excludeProductId) {
      viewedProducts = viewedProducts.filter((/** @type {string} */ id) => id !== excludeProductId);
    }

    if (viewedProducts.length === 0) {
      return;
    }

    const limit = parseInt(maxProducts || '4', 10);
    viewedProducts = viewedProducts.slice(0, limit);

    const query = viewedProducts.map((/** @type {string} */ id) => `id:${id}`).join(' OR ');
    const url = `${this.dataset.url}?q=${encodeURIComponent(query)}&sections=${sectionId}`;

    try {
      const response = await fetch(url);
      if (!response.ok) return;
      const json = await response.json();
      const htmlContent = json[sectionId];
      if (!htmlContent) return;

      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlContent, 'text/html');
      const newContent = doc.querySelector('.resource-list-wrapper');

      if (newContent && newContent.innerHTML.trim().length > 0) {
        const wrapper = this.querySelector('.resource-list-wrapper');
        if (wrapper) {
          wrapper.innerHTML = newContent.innerHTML;
          this.classList.remove('hidden');
          // Dispatch a custom event to notify listeners (e.g. Slick carousel initialization)
          this.dispatchEvent(new CustomEvent('recently-viewed:loaded', { bubbles: true }));
        }
      }
    } catch (e) {
      console.error('Recently viewed products error:', e);
    }
  }
}

if (!customElements.get('recently-viewed')) {
  customElements.define('recently-viewed', RecentlyViewedComponent);
}
